import React, { useEffect, useState } from "react";
import {
    PrimaryButton, DetailsListLayoutMode, IColumn,
    Dropdown, IDropdownOption,
    ShimmeredDetailsList,
    ScrollablePane,
    ConstrainMode,
    TextField,
    Stack,
    DefaultButton,
    MessageBar,
    MessageBarType,
    Rating,
    RatingSize,
    Dialog,
    DialogType,
    DialogFooter,
    Spinner,
    SpinnerSize,
} from "@fluentui/react";
import { useNavigate } from "react-router-dom";
import {
    ImageData,
    DEFAULT_STYLE_OPTIONS,
    ImageFilters,
    UploadImageFormData,
} from "../../types/image";
import {
    fetchImageList,
    uploadImage,
    deleteImage,
    getImageUrls,
} from "../../services/image";
import { UploadImageDialog } from "../../components/UploadImageDialog";
import { ImageDetailDialog } from "../../components/ImageDetailDialog";
import style from "./index.module.scss";

// 分页选项
const pageSizeOptions = [
    { text: "20", key: 20 },
    { text: "30", key: 30 },
    { text: "50", key: 50 },
];

// 使用从types导入的样式选项
const styleOptions: IDropdownOption[] = DEFAULT_STYLE_OPTIONS.map((option) => ({
    key: option.key,
    text: option.text,
}));

// 表格列定义 - 基于新的数据结构
const columns: IColumn[] = [
    {
        key: "imageId",
        name: "Image ID",
        fieldName: "imageId",
        minWidth: 120,
        maxWidth: 150,
        isResizable: true,
    },
    {
        key: "description",
        name: "Description",
        fieldName: "description",
        minWidth: 250,
        maxWidth: 400,
        isResizable: true,
    },
    {
        key: "style",
        name: "Style",
        fieldName: "style",
        minWidth: 100,
        maxWidth: 120,
        isResizable: true,
    },
    {
        key: "tags",
        name: "Tags",
        fieldName: "tags",
        minWidth: 200,
        maxWidth: 300,
        isResizable: true,
    },
    {
        key: "preview",
        name: "Preview",
        fieldName: "preview",
        minWidth: 120,
        maxWidth: 120,
        isResizable: false,
    },
    {
        key: "imageCount",
        name: "Image Count",
        fieldName: "imageCount",
        minWidth: 100,
        maxWidth: 120,
        isResizable: true,
    },
    {
        key: "rating",
        name: "Rating",
        fieldName: "rating",
        minWidth: 120,
        maxWidth: 150,
        isResizable: true,
    },
    {
        key: "score",
        name: "Score",
        fieldName: "score",
        minWidth: 120,
        maxWidth: 150,
        isResizable: true,
    },
    {
        key: "rerankerScore",
        name: "Reranker Score",
        fieldName: "rerankerScore",
        minWidth: 140,
        maxWidth: 160,
        isResizable: true,
    },
    {
        key: "actions",
        name: "Actions",
        fieldName: "actions",
        minWidth: 150,
        maxWidth: 180,
        isResizable: false,
    },
];

export const Image = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<ImageData[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [showShimmer, setShowShimmer] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showUploadDialog, setShowUploadDialog] = useState<boolean>(false);
    const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
    const [imageToDelete, setImageToDelete] = useState<string | null>(null);
    const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [showDetailDialog, setShowDetailDialog] = useState<boolean>(false);
    const [selectedImageForDetail, setSelectedImageForDetail] = useState<ImageData | null>(null);

    // 存储图片预览URL的状态
    const [imagePreviewUrls, setImagePreviewUrls] = useState<Record<string, string>>({});

    // 筛选条件状态 - 基于新的数据结构
    const [filters, setFilters] = useState<ImageFilters>({
        imageId: "",
        style: "",
        description: "",
        tags: [],
        keyword: "", // 新增关键词搜索字段
    });
    // Local input state for the keyword field. We only push this into `filters.keyword` when user triggers search.
    const [keywordInput, setKeywordInput] = useState<string>(filters.keyword || "");
    // 分页状态
    const [pageNum, setPageNum] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);

    // 加载数据函数
    const loadImageData = React.useCallback(async () => {
        try {
            setIsLoading(true);
            setShowShimmer(true);
            setError(null);
            const response = await fetchImageList(filters, pageNum, pageSize);
            console.log("API Response:", response); // 调试信息
            setData(response.images || []);
            setTotal(response.total || 0);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to load image data:", error);
            setError("Failed to load image data. Please try again.");
            setData([]);
        } finally {
            setIsLoading(false);
            setShowShimmer(false);
        }
    }, [filters, pageNum, pageSize]);

    // 处理图片上传
    const handleUpload = async (formData: UploadImageFormData) => {
        try {
            // 转换表单数据为 API 格式
            const uploadData = {
                imageId: formData.imageId,
                description: formData.description,
                style: formData.style,
                tags: formData.tags.split(",").map((tag) => tag.trim()).filter((tag) => tag.length > 0),
                rating: formData.rating,
                files: formData.files,
            };

            await uploadImage(uploadData);
            setUploadSuccess(`Successfully uploaded ${formData.files.length} image(s) for ${formData.imageId}`);

            // 刷新数据列表
            await loadImageData();

            // 自动清除成功消息
            setTimeout(() => setUploadSuccess(null), 5000);
        } catch (uploadError) {
            throw new Error(uploadError instanceof Error ? uploadError.message : "Upload failed");
        }
    };

    // 处理删除图片
    const handleDeleteClick = (imageId: string) => {
        setImageToDelete(imageId);
        setShowDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
        if (!imageToDelete) return;

        try {
            setIsDeleting(true);
            await deleteImage(imageToDelete);
            setDeleteSuccess(`Successfully deleted image: ${imageToDelete}`);

            // 刷新数据列表
            await loadImageData();

            // 自动清除成功消息
            setTimeout(() => setDeleteSuccess(null), 5000);
        } catch (deleteError) {
            setError(deleteError instanceof Error ? deleteError.message : "Delete failed");
        } finally {
            setIsDeleting(false);
            setShowDeleteDialog(false);
            setImageToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        if (isDeleting) return; // 删除中不允许取消
        setShowDeleteDialog(false);
        setImageToDelete(null);
    };

    // 处理查看详情
    const handleDetailClick = (imageData: ImageData) => {
        setSelectedImageForDetail(imageData);
        setShowDetailDialog(true);
    };

    const handleDetailDialogClose = () => {
        setShowDetailDialog(false);
        setSelectedImageForDetail(null);
    };

    const handleImageUpdate = async () => {
        // Refresh the data list to show updated information
        await loadImageData();
    };

    // 初始化数据
    useEffect(() => {
        loadImageData();
    }, [loadImageData]); // 依赖分页参数

    // 当筛选条件改变时，重置到第一页
    useEffect(() => {
        setPageNum(1);
    }, [filters.keyword, filters.style]);

    // Keep local keywordInput in sync when filters.keyword is changed programmatically
    useEffect(() => {
        setKeywordInput(filters.keyword || "");
    }, [filters.keyword]);

    // 获取图片预览URL的函数
    const fetchImagePreview = React.useCallback(async (imageId: string) => {
        if (imagePreviewUrls[imageId]) {
            return; // 已经有缓存，不需要重新获取
        }

        try {
            const urls = await getImageUrls(imageId);
            if (urls.length > 0) {
                setImagePreviewUrls((prev) => ({
                    ...prev,
                    [imageId]: urls[0], // 只取第一张图片
                }));
            }
        } catch (error) {
            console.error(`Failed to fetch preview for ${imageId}:`, error);
        }
    }, [imagePreviewUrls]);

    // 当数据变化时，获取所有图片的预览
    React.useEffect(() => {
        data.forEach((item) => {
            fetchImagePreview(item.imageId);
        });
    }, [data, fetchImagePreview]);

    // 表格行渲染 - 基于新的数据结构
    const onRenderItemColumn = (item: ImageData, index?: number, column?: IColumn) => {
        const fieldContent = item[column?.fieldName as keyof ImageData];

        switch (column?.key) {
            case "preview": {
                const previewUrl = imagePreviewUrls[item.imageId];
                return (
                    <div style={{
                        width: "100px",
                        height: "60px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #e1e1e1",
                        borderRadius: "4px",
                        backgroundColor: "#fafafa",
                        overflow: "hidden",
                    }}
                    >
                        {previewUrl ? (
                            <img
                                src={previewUrl}
                                alt={`Preview of ${item.imageId}`}
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    objectFit: "cover",
                                    borderRadius: "3px",
                                }}
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = "none";
                                    const parent = target.parentElement;
                                    if (parent) {
                                        parent.innerHTML = `
                                            <div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #666; font-size: 12px;">
                                                No Image
                                            </div>
                                        `;
                                    }
                                }}
                            />
                        ) : (
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                                color: "#666",
                                fontSize: "12px",
                            }}
                            >
                                Loading...
                            </div>
                        )}
                    </div>
                );
            }
            case "description":
                return (
                    <div title={item.description} style={{ maxWidth: "350px" }}>
                        {item.description.length > 100
                            ? `${item.description.substring(0, 100)}...`
                            : item.description}
                    </div>
                );
            case "tags":
                return (
                    <div
                        title={item.tags.join(", ")}
                        style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}
                    >
                        {item.tags.slice(0, 3).map((tag) => (
                            <span
                                key={tag}
                                style={{
                                    padding: "2px 6px",
                                    backgroundColor: "#e1f5fe",
                                    color: "#0277bd",
                                    borderRadius: 12,
                                    fontSize: "11px",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {tag}
                            </span>
                        ))}
                        {item.tags.length > 3 && (
                            <span style={{ fontSize: "11px", color: "#605e5c" }}>
                                +
                                {item.tags.length - 3}
                            </span>
                        )}
                    </div>
                );
            case "rating":
                return item.rating !== null ? (
                    <Rating
                        max={5}
                        rating={item.rating}
                        size={RatingSize.Small}
                        readOnly
                        allowZeroStars
                    />
                ) : (
                    <span style={{ color: "#a19f9d" }}>Not rated</span>
                );
            case "score":
                return (typeof item.score === "number" && Number.isFinite(item.score)) ? (
                    <span>{item.score.toFixed(5)}</span>
                ) : (
                    <span style={{ color: "#a19f9d" }}>N/A</span>
                );
            case "rerankerScore":
                return (typeof item.rerankerScore === "number" && Number.isFinite(item.rerankerScore)) ? (
                    <span>{item.rerankerScore.toFixed(3)}</span>
                ) : (
                    <span style={{ color: "#a19f9d" }}>N/A</span>
                );
            case "actions":
                return (
                    <div className={style.btnBox}>
                        <PrimaryButton
                            text="Details"
                            onClick={() => handleDetailClick(item)}
                        />
                        <DefaultButton
                            text="Delete"
                            onClick={() => handleDeleteClick(item.imageId)}
                        />
                    </div>
                );
            default:
                return fieldContent;
        }
    };

    return (
        <div className={style.image}>
            <h1>Image Management</h1>

            {/* 顶部按钮区域 */}
            <Stack horizontal horizontalAlign="space-between" tokens={{ childrenGap: 12 }} styles={{ root: { marginBottom: 24 } }}>
                <Stack horizontal tokens={{ childrenGap: 12 }}>
                    <PrimaryButton
                        text="Upload New Images"
                        iconProps={{ iconName: "Add" }}
                        onClick={() => setShowUploadDialog(true)}
                    />
                    <DefaultButton
                        text="Refresh"
                        iconProps={{ iconName: "Refresh" }}
                        onClick={loadImageData}
                        disabled={isLoading}
                    />
                </Stack>
                <DefaultButton
                    text="Back to Workflow"
                    iconProps={{ iconName: "Back" }}
                    onClick={() => navigate("/workflow")}
                />
            </Stack>

            <Stack tokens={{ childrenGap: 20 }}>
                {/* 筛选条件区域 - 关键词搜索和图片样式 */}
                <div className={style.filterSection}>
                    <Stack
                        horizontal
                        tokens={{ childrenGap: 20 }}
                        horizontalAlign="start"
                        className={style.filterControls}
                        wrap
                    >
                        <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                            <TextField
                                label="Keyword Search"
                                className={style.input}
                                value={keywordInput}
                                onChange={(e, value) => setKeywordInput(value || "")}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        // only update filters (and thereby trigger search) on Enter
                                        setFilters((prev) => ({ ...prev, keyword: keywordInput }));
                                        setPageNum(1);
                                    }
                                }}
                                placeholder="Search keywords in tags or description..."
                            />
                            <Dropdown
                                label="Image Style"
                                className={style.input}
                                options={styleOptions}
                                selectedKey={filters.style || ""}
                                onChange={(e, item) => setFilters((prev) => ({ ...prev, style: item ? String(item.key) : "" }))}
                            />
                            <PrimaryButton
                                text="Search"
                                onClick={() => {
                                    setFilters((prev) => ({ ...prev, keyword: keywordInput }));
                                    setPageNum(1);
                                }}
                                disabled={keywordInput.trim().length === 0 && !(filters.style && filters.style.length)}
                            />
                        </div>
                    </Stack>
                </div>

                {/* 错误信息显示 */}
                {error && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        onDismiss={() => setError(null)}
                        dismissButtonAriaLabel="Close"
                    >
                        {error}
                    </MessageBar>
                )}

                {/* 成功信息显示 */}
                {uploadSuccess && (
                    <MessageBar
                        messageBarType={MessageBarType.success}
                        onDismiss={() => setUploadSuccess(null)}
                        dismissButtonAriaLabel="Close"
                    >
                        {uploadSuccess}
                    </MessageBar>
                )}

                {/* 删除成功信息显示 */}
                {deleteSuccess && (
                    <MessageBar
                        messageBarType={MessageBarType.success}
                        onDismiss={() => setDeleteSuccess(null)}
                        dismissButtonAriaLabel="Close"
                    >
                        {deleteSuccess}
                    </MessageBar>
                )}

                {/* 数据表格区域 */}
                <div style={{ position: "relative", height: "calc(100vh - 320px)" }}>
                    {/* Loading overlay */}
                    {isLoading && (
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: "rgba(255, 255, 255, 0.8)",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                zIndex: 1000,
                            }}
                        >
                            <Stack tokens={{ childrenGap: 12 }} horizontalAlign="center">
                                <Spinner
                                    size={SpinnerSize.large}
                                    label="Loading image data..."
                                    labelPosition="bottom"
                                />
                            </Stack>
                        </div>
                    )}
                    <ScrollablePane>
                        <div style={{ overflowX: "auto" }}>
                            <ShimmeredDetailsList
                                className={style.detailListSticky}
                                items={data}
                                columns={columns}
                                setKey="id"
                                layoutMode={DetailsListLayoutMode.fixedColumns}
                                constrainMode={ConstrainMode.unconstrained}
                                onRenderItemColumn={onRenderItemColumn}
                                isHeaderVisible
                                selectionMode={0}
                                enableShimmer={showShimmer}
                            />
                        </div>
                    </ScrollablePane>
                </div>

                {/* 分页控制区域 */}
                <div className={style.pag}>
                    <span style={{ marginRight: 20, fontSize: "14px", color: "#605e5c" }}>
                        Total:
                        {" "}
                        {total}
                        {" "}
                        | Page:
                        {" "}
                        {pageNum}
                        {" "}
                        /
                        {" "}
                        {Math.ceil(total / pageSize) || 1}
                    </span>
                    <Dropdown
                        options={pageSizeOptions}
                        selectedKey={pageSize}
                        style={{ width: 200, marginRight: 20 }}
                        onChange={(e, item) => setPageSize(Number(item?.key))}
                    />
                    <button
                        type="button"
                        className={style.nextPage}
                        onClick={() => setPageNum(pageNum > 1 ? pageNum - 1 : 1)}
                        disabled={pageNum <= 1}
                        style={{
                            opacity: pageNum <= 1 ? 0.5 : 1,
                            cursor: pageNum <= 1 ? "not-allowed" : "pointer",
                        }}
                    >
                        &lt;
                    </button>
                    <span className={style.nextPage}>
                        {pageNum}
                    </span>
                    <button
                        type="button"
                        className={style.nextPage}
                        onClick={() => {
                            const totalPages = Math.ceil(total / pageSize);
                            if (pageNum < totalPages) {
                                setPageNum(pageNum + 1);
                            }
                        }}
                        disabled={pageNum >= Math.ceil(total / pageSize)}
                        style={{
                            opacity: pageNum >= Math.ceil(total / pageSize) ? 0.5 : 1,
                            cursor: pageNum >= Math.ceil(total / pageSize) ? "not-allowed" : "pointer",
                        }}
                    >
                        &gt;
                    </button>
                </div>
            </Stack>

            {/* 上传对话框 */}
            <UploadImageDialog
                isOpen={showUploadDialog}
                onDismiss={() => setShowUploadDialog(false)}
                onUpload={handleUpload}
            />

            {/* 删除确认对话框 */}
            <Dialog
                hidden={!showDeleteDialog}
                onDismiss={handleDeleteCancel}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: "Confirm Delete",
                    subText: isDeleting ? "Deleting image, please wait..." : "Are you sure you want to delete this image? This action cannot be undone.",
                }}
                modalProps={{
                    isBlocking: isDeleting,
                    styles: { main: { maxWidth: 450 } },
                }}
            >
                {isDeleting && (
                    <Stack tokens={{ childrenGap: 16 }} horizontalAlign="center" styles={{ root: { padding: "20px 0" } }}>
                        <Spinner
                            size={SpinnerSize.large}
                            label="Deleting..."
                            labelPosition="bottom"
                        />
                    </Stack>
                )}
                <DialogFooter>
                    <PrimaryButton
                        onClick={handleDeleteConfirm}
                        text={isDeleting ? "Deleting..." : "Delete"}
                        disabled={isDeleting}
                        styles={{
                            root: {
                                backgroundColor: "#d13438",
                                borderColor: "#d13438",
                            },
                            rootHovered: {
                                backgroundColor: "#a4262c",
                                borderColor: "#a4262c",
                            },
                        }}
                    />
                    <DefaultButton
                        onClick={handleDeleteCancel}
                        text="Cancel"
                        disabled={isDeleting}
                    />
                </DialogFooter>
            </Dialog>

            {/* 图片详情对话框 */}
            <ImageDetailDialog
                isOpen={showDetailDialog}
                imageId={selectedImageForDetail?.imageId || null}
                imageData={selectedImageForDetail}
                onDismiss={handleDetailDialogClose}
                onUpdate={handleImageUpdate}
            />
        </div>
    );
};
